import { getCookieConsent } from '../components/CookieConsent'
import { useAuthStore } from '../store/authStore'

/**
 * Seznam.cz / Sklik retargeting (rc.js).
 *
 * Marketingové cookies — hit se odpálí POUZE pokud má návštěvník souhlas 'all'
 * (getCookieConsent). Bez souhlasu se skript ani nenačte (GDPR + pravidla Seznamu).
 * Skript se načítá lazy a jen jednou; hit posíláme při každém pageview (SPA).
 */
const RTG_ID = 1695446
/** Identifikátor konverze Sklik — registrace uživatele. */
const CONVERSION_REGISTER_ID = 100280307
/** Přiřazená hodnota registrace v Kč (odhad, registrace je zdarma). */
const CONVERSION_REGISTER_VALUE = 400
/** Klíč pro odloženou konverzi (přežije full reload, např. po OAuth callbacku). */
const PENDING_CONVERSION_KEY = 'seznam_conv_pending'

declare global {
  interface Window {
    rc?: {
      retargetingHit?: (conf: { rtgId: number; consent: 0 | 1 }) => void
      conversionHit?: (conf: { id: number; value?: number; consent: 0 | 1 }) => void
    }
    sznIVA?: { IS?: { updateIdentities?: (ids: { eid: string | null }) => void } }
  }
}

let scriptPromise: Promise<void> | null = null

function loadRcScript(): Promise<void> {
  if (window.rc?.retargetingHit) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = 'https://c.seznam.cz/js/rc.js'
    s.async = true
    s.onload = () => resolve()
    s.onerror = () => { scriptPromise = null; reject(new Error('Seznam rc.js se nepodařilo načíst')) }
    document.head.appendChild(s)
  })
  return scriptPromise
}

/**
 * Spočítá `eid` pro Seznam IS = SHA-256 hash e-mailu přihlášeného uživatele.
 * Pro nepřihlášeného (nebo bez Web Crypto) vrací null — Seznam to akceptuje.
 */
async function currentEid(): Promise<string | null> {
  const email = useAuthStore.getState().user?.email?.trim().toLowerCase()
  if (!email || !globalThis.crypto?.subtle) return null
  try {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(email))
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('')
  } catch {
    return null
  }
}

/** Odešle retargetingový hit, pokud má návštěvník souhlas 'all'. Jinak nedělá nic. */
export function seznamRetargetingHit(): void {
  if (getCookieConsent() !== 'all') return
  loadRcScript()
    .then(async () => {
      try {
        window.sznIVA?.IS?.updateIdentities?.({ eid: await currentEid() })
        window.rc?.retargetingHit?.({ rtgId: RTG_ID, consent: 1 })
      } catch { /* ignore */ }
    })
    .catch(() => { /* ignore — výpadek skriptu nesmí shodit appku */ })
}

/**
 * Odešle konverzní hit (registrace uživatele), pokud má návštěvník souhlas 'all'.
 * Volat po úspěšné registraci přes client-side navigaci (ne před full reloadem).
 */
export function seznamConversionHit(value: number = CONVERSION_REGISTER_VALUE): void {
  if (getCookieConsent() !== 'all') return
  loadRcScript()
    .then(async () => {
      try {
        window.sznIVA?.IS?.updateIdentities?.({ eid: await currentEid() })
        window.rc?.conversionHit?.({ id: CONVERSION_REGISTER_ID, value, consent: 1 })
      } catch { /* ignore */ }
    })
    .catch(() => { /* ignore */ })
}

/** Zafrontuje konverzi přežívající full reload (např. OAuth callback dělá window.location.assign). */
export function queueSeznamConversion(): void {
  try { sessionStorage.setItem(PENDING_CONVERSION_KEY, '1') } catch { /* ignore */ }
}

/** Po startu appky odpálí odloženou konverzi, pokud nějaká čeká. Volat jednou v App. */
export function flushPendingSeznamConversion(): void {
  try {
    if (sessionStorage.getItem(PENDING_CONVERSION_KEY)) {
      sessionStorage.removeItem(PENDING_CONVERSION_KEY)
      seznamConversionHit()
    }
  } catch { /* ignore */ }
}
